// Journey pipeline types
//
// `journey_prospects` tracks a single intro email all the way to a signed client.
// Rows are created by the pp_worker automation when it detects an intro thread;
// humans move them along from the Journey board. `journey_actions` is the
// worker's own append-only worklog for a journey and is read-only in the app.
//
// The 14 stages below must stay in sync with the journey_prospects_stage_check
// constraint in the database.

export type JourneyStage =
  | 'intro_detected'
  | 'intro_replied'
  | 'booked'
  | 'brief_built'
  | 'call_done'
  | 'access_requested'
  | 'access_partial'
  | 'access_complete'
  | 'audit_building'
  | 'audit_published'
  | 'proposal_sent'
  | 'closed_won'
  | 'lost'
  | 'stalled';

export const JOURNEY_STAGE_LABELS: Record<JourneyStage, string> = {
  intro_detected: 'Intro Detected',
  intro_replied: 'Intro Replied',
  booked: 'Booked',
  brief_built: 'Brief Built',
  call_done: 'Call Done',
  access_requested: 'Access Requested',
  access_partial: 'Access Partial',
  access_complete: 'Access Complete',
  audit_building: 'Audit Building',
  audit_published: 'Audit Published',
  proposal_sent: 'Proposal Sent',
  closed_won: 'Closed Won',
  lost: 'Lost',
  stalled: 'Stalled',
};

// The board groups the 14 stages into 6 readable columns. Fourteen columns is
// not a board, it's a spreadsheet — the stage badge on each card keeps the
// precise stage visible without costing a column.
export interface JourneyPhase {
  key: string;
  label: string;
  description: string;
  stages: JourneyStage[];
}

export const JOURNEY_PHASES: JourneyPhase[] = [
  {
    key: 'intro',
    label: 'Intro',
    description: 'Connector made the intro; getting to a booked call',
    stages: ['intro_detected', 'intro_replied', 'booked'],
  },
  {
    key: 'meeting',
    label: 'Meeting',
    description: 'Prep and the discovery call itself',
    stages: ['brief_built', 'call_done'],
  },
  {
    key: 'access',
    label: 'Access',
    description: 'Collecting platform access for the audit',
    stages: ['access_requested', 'access_partial', 'access_complete'],
  },
  {
    key: 'audit',
    label: 'Audit',
    description: 'Building and delivering the audit',
    stages: ['audit_building', 'audit_published'],
  },
  {
    key: 'close',
    label: 'Close',
    description: 'Proposal out, waiting on signature',
    stages: ['proposal_sent', 'closed_won'],
  },
  {
    key: 'inactive',
    label: 'Inactive',
    description: 'No longer progressing',
    stages: ['lost', 'stalled'],
  },
];

export const JOURNEY_STAGE_ORDER: JourneyStage[] = JOURNEY_PHASES.flatMap((p) => p.stages);

export function phaseForStage(stage: JourneyStage): JourneyPhase | undefined {
  return JOURNEY_PHASES.find((p) => p.stages.includes(stage));
}

export interface JourneyProspect {
  id: string;
  prospect_email: string;
  connector_email: string | null;
  company_name: string | null;
  person_name: string | null;
  slug: string | null;
  stage: JourneyStage;
  intro_thread_id: string;
  intro_message_id: string;
  intro_account: string;
  calendar_event_id: string | null;
  meeting_start_at: string | null;
  meeting_end_at: string | null;
  otter_otid: string | null;
  classifier_confidence: number | null;
  client_id: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export type JourneyActionStatus = 'claimed' | 'done' | 'failed';

export interface JourneyAction {
  id: number;
  journey_id: string;
  dedupe_key: string;
  status: JourneyActionStatus;
  detail: Record<string, unknown> | null;
  created_at: string | null;
  updated_at: string | null;
}

// Display name for a journey row: the person if the classifier found one,
// otherwise fall back to the email it was detected from.
export function journeyDisplayName(j: JourneyProspect): string {
  return j.person_name || j.prospect_email;
}
